import { createContext, useContext, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useBreakpoint } from '../responsive/useBreakpoint'
import { colSpan, fittedColumns, heightPx, rowSpan, type Cols, type WidgetLayoutItem } from './gridMaths'
import './widgetGrid.css'

const WidgetGridContext = createContext<Cols>(1)

type Props<Id extends string> = {
  items: readonly WidgetLayoutItem<Id>[]
  render: (id: Id) => ReactNode
  label?: string
}

/**
 * The page's widget grid: a measured column count (1-4), explicit heights and
 * clamped spans. `phone` is one column whatever was measured (M0-R27).
 *
 * Forward compatibility, all additive and none built yet: `items` is controlled
 * data so a future `useWidgetLayout(page, defaults)` can own the visible and
 * hidden lists in localStorage; edit mode adds `editing` and `onChange`;
 * `data-widget-id` is the handle `@dnd-kit` will key on; and `Widget` is the one
 * place cell chrome (badge, handle, grabbers) will mount.
 */
export function WidgetGrid<Id extends string>({ items, render, label }: Props<Id>) {
  const phone = useBreakpoint() === 'phone'
  const ref = useRef<HTMLDivElement>(null)
  const [measured, setMeasured] = useState<Cols>(1)
  useLayoutEffect(() => {
    const el = ref.current
    if (el === null) return
    setMeasured(fittedColumns(el.clientWidth))
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setMeasured(fittedColumns(el.clientWidth)))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const n: Cols = phone ? 1 : measured
  return (
    <WidgetGridContext.Provider value={n}>
      <div ref={ref} className={n === 1 ? 'wg wg--single' : 'wg'} style={{ '--wg-cols': n } as CSSProperties} role={label === undefined ? undefined : 'group'} aria-label={label}>
        {items.map((item) => (
          <Widget key={item.id} id={item.id} w={item.w} h={item.h}>
            {render(item.id)}
          </Widget>
        ))}
      </div>
    </WidgetGridContext.Provider>
  )
}

export function Widget({ id, w, h, children }: { id: string; w: Cols; h: WidgetLayoutItem<string>['h']; children: ReactNode }) {
  const n = useContext(WidgetGridContext)
  // The explicit height exists to stop multi-column collapse. At one column there is nothing to
  // collapse against, so the cell is as tall as its content.
  const geometry: Record<string, string | number> = { '--wg-col-span': colSpan(w, n) }
  if (n > 1) {
    geometry['--wg-row-span'] = rowSpan(h)
    geometry['--wg-h'] = `${heightPx(h)}px`
  }
  return (
    <div className="wg-cell" data-widget-id={id} style={geometry as CSSProperties}>
      {children}
    </div>
  )
}
